import { Button } from '@/components/Button/Button';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import type { TemplateCategory } from '@/graphql/message-templates';

import styles from './TemplateFilterBar.module.css';
import {
  categoryOption,
  type CategoryFilter,
  type ScopeFilter,
  type StatusFilter,
  type TemplateFilters,
} from './TemplateFilterBar.types';

export interface TemplateFilterBarProps {
  filters: TemplateFilters;
  /** Keystone's own enumeration over both scopes, merged by the container.
   *  Its counts are what the options are labelled with. */
  categories: readonly TemplateCategory[];
  onChange: (filters: TemplateFilters) => void;
  onReset: () => void;
}

export function TemplateFilterBar({
  filters,
  categories,
  onChange,
  onReset,
}: TemplateFilterBarProps) {
  const set = <K extends keyof TemplateFilters>(key: K, value: TemplateFilters[K]) =>
    onChange({ ...filters, [key]: value });

  const ungrouped = categories.find((row) => row.category === null);
  const named = categories.filter((row) => row.category !== null);

  return (
    <div className={styles.bar}>
      <Select
        label="Scope"
        onChange={(value) => set('scope', value as ScopeFilter)}
        options={[
          ['any', 'Both lists'],
          ['mine', 'Mine'],
          ['brokerage', 'Brokerage'],
        ]}
        value={filters.scope}
      />

      <Select
        hint="Keystone offers only ACTIVE when nothing asks; the harness asks for both, because a reorder has to name every live template."
        label="Status"
        onChange={(value) => set('status', value as StatusFilter)}
        options={[
          ['any', 'Any status'],
          ['ACTIVE', 'Active'],
          ['DRAFT', 'Draft'],
        ]}
        value={filters.status}
      />

      <Select
        hint="Keystone's own enumeration, over both statuses. It has no vocabulary for these, so a typo makes a group."
        label="Category"
        onChange={(value) => set('category', value as CategoryFilter)}
        options={[
          ['any', 'Any category'],
          ...(ungrouped ? [['ungrouped', `Ungrouped (${ungrouped.templateCount})`] as const] : []),
          ...named.map(
            (row) =>
              [
                categoryOption(row.category as string),
                `${row.category} (${row.templateCount})`,
              ] as const,
          ),
        ]}
        value={filters.category}
      />

      <TextField
        hint="Matched here, not at Keystone: neither list field takes a title."
        label="Title contains"
        onChange={(event) => set('search', event.target.value)}
        placeholder="chase"
        value={filters.search}
      />

      <div className={styles.actions}>
        <Button onClick={onReset} type="button" variant="ghost">
          Reset
        </Button>
      </div>
    </div>
  );
}
